import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import { getRequest, postRequest, patchRequest, deleteRequest, server } from "../config";

const PromotionalAds = () => {
  const [ads, setAds] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form fields according to the schema provided
  const [promotionalAdsId, setPromotionalAdsId] = useState("");
  const [adName, setAdName] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [status, setStatus] = useState(true);
  const [categoryId, setCategoryId] = useState("");
  const [adCategory, setAdCategory] = useState("");

  useEffect(() => {
    fetchAds();
    fetchCategories();
  }, []);

  const fetchAds = async () => {
    // ASSUMPTION: backend endpoint is `${server.uri}promotional-ads`
    const res = await getRequest(`${server.uri}promotional-ads`);
    if (res) {
      setAds(res);
      setAllAds(res);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await getRequest(`${server.uri}categories`);
      if (res) {
        setCategories(res);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPromotionalAdsId("");
    setAdName("");
    setBannerUrl("");
    setMinPrice("");
    setMaxPrice("");
    setDiscountPercentage("");
    setStatus(true);
    setCategoryId("");
    setAdCategory("");
  };

  const handleAddAd = () => {
    resetForm();
    setEditingAd(null);
    setOpenDialog(true);
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setPromotionalAdsId(ad.promotionalAdsId || "");
    setAdName(ad.adName || "");
    setBannerUrl(ad.bannerUrl || "");
    setMinPrice(ad.adConfig?.minPrice || "");
    setMaxPrice(ad.adConfig?.maxPrice || "");
    setDiscountPercentage(ad.adConfig?.discountPercentage || "");
    setStatus(ad.status ?? true);
    setCategoryId(ad.categoryId || "");
    setAdCategory(ad.adCategory || "");
    setOpenDialog(true);
  };

  const handleCategoryChange = (event) => {
    const selectedCategoryId = event.target.value;
    const selectedCategory = categories.find(cat => cat.categoryId === selectedCategoryId);
    setCategoryId(selectedCategoryId);
    setAdCategory(selectedCategory ? selectedCategory.name : "");
  };

  const handleDeleteAd = async (id) => {
    if (window.confirm("Are you sure you want to delete this ad?")) {
      await deleteRequest(`${server.uri}promotional-ads/${id}`);
      fetchAds();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveAd = async () => {
    const payload = {
      promotionalAdsId: promotionalAdsId || `${Date.now()}`,
      adName,
      bannerUrl,
      adConfig: {
        minPrice: String(minPrice),
        maxPrice: String(maxPrice),
        discountPercentage: String(discountPercentage),
      },
      status,
      createdAt: new Date().toISOString(),
      categoryId,
      adCategory,
    };

    if (editingAd) {
      await patchRequest(`${server.uri}promotional-ads/${editingAd.promotionalAdsId || editingAd._id || editingAd.id}`, payload);
    } else {
      await postRequest(`${server.uri}promotional-ads`, payload);
    }

    fetchAds();
    handleDialogClose();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = allAds.filter((a) => (a.adName || "").toLowerCase().includes(value));
    setAds(filtered);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginTop: 8, marginBottom: 2 }}>
        Promotional Ads
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search by ad name"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ color: "gray", marginRight: 1 }} />,
          }}
          sx={{ width: "60%" }}
        />
        <Button variant="contained" onClick={handleAddAd}>
          Add Promotional Ad
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
              <TableRow>
                <TableCell><strong>Ad Name</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Price Range</strong></TableCell>
                <TableCell><strong>Discount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ads.map((ad) => {
                const category = categories.find(cat => cat.categoryId === ad.categoryId);
                return (
                  <TableRow key={ad.promotionalAdsId || ad._id || ad.id}>
                    <TableCell>{ad.adName}</TableCell>
                    <TableCell>{category?.name || ad.adCategory || "N/A"}</TableCell>
                    <TableCell>{ad.adConfig ? `${ad.adConfig.minPrice} - ${ad.adConfig.maxPrice}` : "-"}</TableCell>
                    <TableCell>{ad.adConfig?.discountPercentage || "-"}%</TableCell>
                    <TableCell>{ad.status ? "Active" : "Inactive"}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditAd(ad)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDeleteAd(ad.promotionalAdsId || ad._id || ad.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {ads.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No promotional ads found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAd ? "Edit Promotional Ad" : "Add New Promotional Ad"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Ad Name" value={adName} onChange={(e) => setAdName(e.target.value)} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth label="Banner URL" value={bannerUrl} onChange={(e) => setBannerUrl(e.target.value)} sx={{ mb: 2 }} />
          <Box display="flex" gap={2}>
            <TextField fullWidth type="number" label="Min Price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth type="number" label="Max Price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} sx={{ mb: 2 }} />
          </Box>
          <TextField fullWidth type="number" label="Discount Percentage" value={discountPercentage} onChange={(e) => setDiscountPercentage(e.target.value)} sx={{ mb: 2 }} />
          
          <FormControl fullWidth sx={{ mb: 2 }} disabled={loading}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryId}
              label="Category"
              onChange={handleCategoryChange}
            >
              {loading ? (
                <MenuItem disabled>Loading categories...</MenuItem>
              ) : categories.length === 0 ? (
                <MenuItem disabled>No categories available</MenuItem>
              ) : (
                categories.map((category) => (
                  <MenuItem key={category.categoryId} value={category.categoryId}>
                    {category.name}-{category?.categoryType=='monthly'?"Contract":"Daily" || ""}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={status} onChange={(e) => setStatus(e.target.checked)} />}
            label={status ? "Active" : "Inactive"}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveAd} variant="contained">
            {editingAd ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromotionalAds;
